import { useEffect, useState } from 'react';
import { getMySettings, type MailAddrDisplay, type MailComposeWindow, type MailListDensity } from '../../api/users';
import type { MailFolder } from '../../api/mail';
import { registerSessionCache } from '../../lib/session-caches';

/**
 * 개인 메일 설정 읽기 (migration 110, 2026-08-20).
 *
 * 메일 화면 세 곳(MailPage · ComposeModal · MailDetail)이 같은 값을 본다. 화면마다 따로
 * 부르면 창을 열 때마다 같은 요청이 세 번 나가므로, **한 번 부른 것을 모듈에 담아 나눠 쓴다**.
 *
 * ★ 기본값이 곧 **지금까지의 동작**이다. 서버가 이 키들을 모르거나(구 서버) 요청이 실패하면
 *   기본값으로 떨어져, 설정이 없던 때와 똑같이 동작한다 — 화면이 비거나 멈추지 않는다.
 */

export interface MailPrefs {
  firstFolder: MailFolder;
  pageSize: number;
  composeMode: 'HTML' | 'TEXT' | 'MARKDOWN';
  quoteDivider: boolean;
  simplifySubject: boolean;
  /* ── 「환경설정 > 기본환경」 (migration 158, 2026-09-05) ──
     화면 셋(MailPage · MailDetail · ComposeModal)이 나눠 쓰는 값만 여기 둔다.
     설정 화면 자신은 `mailSettings.ts` 로 전부를 읽는다 — 저장 직전 값이 필요해서다. */
  /** 목록·상세가 보낸 사람을 어떻게 적나. */
  addrDisplay: MailAddrDisplay;
  /** 본문의 이미지를 아예 안 그린다. */
  blockImages: boolean;
  /** 보내기를 누르면 한 번 물어본다. */
  sendConfirm: boolean;
  /** 쓰기를 화면으로 여나 팝업으로 여나. */
  composeWindow: MailComposeWindow;
  /** 목록 밀도 — 기기 사본(mailView)의 «진실» 쪽이다. */
  listDensity: MailListDensity;
}

/** 지금까지 코드에 박혀 있던 값 그대로 — 이 표가 곧 "설정 전" 동작이다. */
export const MAIL_PREF_DEFAULTS: MailPrefs = {
  firstFolder: 'inbox',      // MailPage 의 useState('inbox')
  pageSize: 50,              // listMail(folder, { limit: 50 })
  composeMode: 'TEXT',       // ComposeModal 의 bodyType: 'TEXT'
  quoteDivider: true,        // MailDetail 이 항상 인용을 붙이던 것
  simplifySubject: false,    // 지금은 RE:/FW: 를 줄이지 않는다
  /* ★ 여기 값은 「서버가 못 대답할 때」다 — «새 행의 기본값»(migration 158)과 뜻이 다르다.
       그래서 composeMode 는 위에서 'TEXT' 로 남는다: 서버가 죽었을 때 쓰기 모양이
       말없이 바뀌면 안 된다. 아래 다섯은 두 값이 마침 같다. */
  addrDisplay: 'NAME_EMAIL', // 상세가 지금도 「이름 <주소>」로 적는다
  blockImages: false,        // 지금까지 이미지를 그대로 그렸다
  sendConfirm: true,         // ★ 2026-09-05 신설 — 이 판부터 한 번 물어본다
  composeWindow: 'PAGE',     // 쓰기는 2026-08-22 부터 화면이다
  listDensity: 'NARROW',     // 아바타·미리보기 없는 압축형
};

let cached: Promise<MailPrefs> | null = null;

function fetchPrefs(): Promise<MailPrefs> {
  return getMySettings()
    .then((s): MailPrefs => ({
      firstFolder: s.mailFirstFolder ?? MAIL_PREF_DEFAULTS.firstFolder,
      pageSize: s.mailPageSize ?? MAIL_PREF_DEFAULTS.pageSize,
      composeMode: s.mailComposeMode ?? MAIL_PREF_DEFAULTS.composeMode,
      quoteDivider: s.mailQuoteDivider ?? MAIL_PREF_DEFAULTS.quoteDivider,
      simplifySubject: s.mailSimplifySubject ?? MAIL_PREF_DEFAULTS.simplifySubject,
      addrDisplay: s.mailAddrDisplay ?? MAIL_PREF_DEFAULTS.addrDisplay,
      blockImages: s.mailBlockImages ?? MAIL_PREF_DEFAULTS.blockImages,
      sendConfirm: s.mailSendConfirm ?? MAIL_PREF_DEFAULTS.sendConfirm,
      composeWindow: s.mailComposeWindow ?? MAIL_PREF_DEFAULTS.composeWindow,
      listDensity: s.mailListDensity ?? MAIL_PREF_DEFAULTS.listDensity,
    }))
    .catch(() => MAIL_PREF_DEFAULTS);
}

export function loadMailPrefs(): Promise<MailPrefs> {
  cached ??= fetchPrefs();
  return cached;
}

/** 설정 화면에서 값을 바꾼 뒤 부른다 — 다음에 읽을 때 새로 가져온다. */
export function invalidateMailPrefs(): void {
  cached = null;
}

/** 테스트에서 캐시를 비운다. */
export function _resetMailPrefsForTests(): void {
  cached = null;
}

/**
 * 값이 도착하기 전에는 기본값을 준다 — 화면이 비어 있는 상태로 기다리지 않게.
 * `ready` 는 "서버 값이 반영됐는가" 이며, 첫 화면처럼 **한 번만** 적용해야 하는 것이 이 값을 본다.
 */
export function useMailPrefs(): { prefs: MailPrefs; ready: boolean } {
  const [prefs, setPrefs] = useState<MailPrefs>(MAIL_PREF_DEFAULTS);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadMailPrefs().then((p) => {
      if (cancelled) return;
      setPrefs(p);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  return { prefs, ready };
}

/**
 * 제목에서 겹친 답장·전달 표시를 한 번으로 줄인다.
 *
 * `RE: RE: Re: 회의록` → `RE: 회의록`. 원문 제목은 건드리지 않고 **보이는 것만** 줄인다.
 * 접두가 없으면 원래 문자열을 그대로 돌려준다(참조 동일 — 불필요한 렌더 방지).
 */
export function simplifySubject(subject: string): string {
  const m = /^((?:\s*(?:RE|FW|FWD)\s*:\s*)+)(.*)$/i.exec(subject);
  if (!m) return subject;
  const first = /^\s*(RE|FW|FWD)\s*:/i.exec(m[1]!)?.[1]?.toUpperCase() ?? 'RE';
  return `${first}: ${m[2]!.trim()}`;
}

// ★★ 로그아웃하면 비운다 (2026-08-31) — **내** 메일 설정이다(첫 폴더 · 한 쪽 크기).
registerSessionCache(invalidateMailPrefs);
